/* Hero Split Slider
   - <hero-split-slider>: fade/slide image carousel (autoplay, arrows, dots, swipe)
   - <offer-bar>: live discount countdowns per offer + optional rotation
*/

(function () {
  'use strict';

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------ */
  /* Slider                                                        */
  /* ------------------------------------------------------------ */

  class HeroSplitSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.hero-split__track');
      this.slides = Array.from(this.querySelectorAll('.hero-split__slide'));
      this.dots = Array.from(this.querySelectorAll('.hero-split__dot'));
      this.transition = this.dataset.transition || 'fade';
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.autoplayDelay = parseInt(this.dataset.interval, 10) || 5000;
      this.timer = null;
      this.pausedByUser = false;

      if (this.slides.length <= 1) return;

      this.style.touchAction = 'pan-y';

      this.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
        button.addEventListener('click', () => {
          this.goTo(this.index + (button.name === 'next' ? 1 : -1));
          this.restartAutoplay();
        });
      });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.goTo(parseInt(dot.dataset.index, 10));
          this.restartAutoplay();
        });
      });

      this.bindSwipe();

      this.onVisibility = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay());
      document.addEventListener('visibilitychange', this.onVisibility);

      this.addEventListener('pointerenter', () => this.stopAutoplay());
      this.addEventListener('pointerleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      });

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (event) => {
          const slide = event.target.closest && event.target.closest('.hero-split__slide');
          if (!slide || !this.contains(slide)) return;
          this.pausedByUser = true;
          this.stopAutoplay();
          this.goTo(this.slides.indexOf(slide));
        };
        this.onBlockDeselect = (event) => {
          if (!this.contains(event.target)) return;
          this.pausedByUser = false;
          this.startAutoplay();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      this.goTo(this.index);
      this.startAutoplay();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    bindSwipe() {
      let startX = 0;
      let startY = 0;
      let tracking = false;

      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
        tracking = true;
      });

      this.addEventListener('pointerup', (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          this.goTo(this.index + (dx < 0 ? 1 : -1));
          this.restartAutoplay();
        }
      });

      this.addEventListener('pointercancel', () => (tracking = false));
    }

    goTo(rawIndex) {
      const count = this.slides.length;
      const index = ((rawIndex % count) + count) % count;
      this.index = index;

      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
        const link = slide.querySelector('.hero-split__slide-link');
        if (link) link.tabIndex = active ? 0 : -1;
      });

      if (this.transition === 'slide' && this.track) {
        this.track.style.transform = 'translateX(' + index * -100 + '%)';
      }

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });
    }

    startAutoplay() {
      if (!this.hasAttribute('data-autoplay') || this.pausedByUser || REDUCED_MOTION.matches) return;
      if (this.timer || document.hidden) return;
      this.timer = setInterval(() => this.goTo(this.index + 1), this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  /* ------------------------------------------------------------ */
  /* Offers bar                                                    */
  /* ------------------------------------------------------------ */

  class OfferBar extends HTMLElement {
    connectedCallback() {
      this.offers = Array.from(this.querySelectorAll('.hero-split__offer'));
      this.offersList = this.querySelector('.hero-split__offers-list');
      this.countdowns = [];
      this.timerRow = this.querySelector('[data-shared-timer]');
      this.timerEls = this.timerRow
        ? {
            days: this.timerRow.querySelector('[data-t-days]'),
            hours: this.timerRow.querySelector('[data-t-hours]'),
            mins: this.timerRow.querySelector('[data-t-mins]'),
            secs: this.timerRow.querySelector('[data-t-secs]'),
            daysUnit: this.timerRow.querySelector('[data-unit-days]'),
            daysSep: this.timerRow.querySelector('[data-sep-days]')
          }
        : null;

      this.sharedTarget = this.hasAttribute('data-shared-end-date')
        ? this.parseSharedDate(this.dataset.sharedEndDate, this.dataset.sharedEndTime)
        : null;

      /* Namespaced attribute — the theme's own countdown in theme.js hijacks
         any bare [data-countdown] element and overwrites its content. */
      if (!this.hasAttribute('data-shared-end-date')) {
        this.offers.forEach((offer) => {
          if (!offer.hasAttribute('data-offer-countdown')) return;
          const target = this.parseDate(offer.dataset.endDate, offer.dataset.endTime);
          if (!target) return;
          this.countdowns.push({ offer: offer, target: target, done: false });
        });
      }

      if ((this.sharedTarget || this.countdowns.length) && this.timerEls) {
        this.tick();
        if (!this.countdownComplete) this.clock = setInterval(() => this.tick(), 1000);
      }

      this.rotateDelay = parseInt(this.dataset.rotateInterval, 10) || 4000;
      if (this.dataset.display === 'rotate') {
        this.currentRotation = 0;
        this.updateRotation();
        this.startRotation();
        this.addEventListener('pointerenter', () => this.stopRotation());
        this.addEventListener('pointerleave', () => this.startRotation());
        this.addEventListener('focusin', () => this.stopRotation());
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.startRotation();
        });
      }

      if (window.Shopify && window.Shopify.designMode) {
        this.onBlockSelect = (event) => {
          const offer = event.target.closest && event.target.closest('.hero-split__offer');
          if (!offer || !this.contains(offer)) return;
          this.stopRotation();
          if (this.dataset.display === 'rotate') {
            const visible = this.visibleOffers();
            const i = visible.indexOf(offer);
            if (i > -1) {
              this.currentRotation = i;
              this.updateRotation();
            }
          }
        };
        this.onBlockDeselect = (event) => {
          if (this.contains(event.target)) this.startRotation();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    disconnectedCallback() {
      this.stopClock();
      this.stopRotation();
      if (this.onBlockSelect) document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.onBlockDeselect) document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    parseDate(date, time) {
      if (!date) return null;
      const offset = this.dataset.utcOffset || '';
      if (offset && !/^[+-]\d{2}:\d{2}$/.test(offset)) return null;
      const parsed = new Date(date + 'T' + (time || '23:59') + ':00' + offset);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    parseSharedDate(date, time) {
      const dateValue = String(date || '').trim();
      const timeValue = String(time === undefined || time === '' ? '2359' : time).trim().padStart(4, '0');
      if (!/^\d{8}$/.test(dateValue) || !/^\d{4}$/.test(timeValue)) return null;

      const year = parseInt(dateValue.slice(0, 4), 10);
      const month = parseInt(dateValue.slice(4, 6), 10);
      const day = parseInt(dateValue.slice(6, 8), 10);
      const hours = parseInt(timeValue.slice(0, 2), 10);
      const minutes = parseInt(timeValue.slice(2, 4), 10);
      if (month < 1 || month > 12 || day < 1 || day > 31 || hours > 23 || minutes > 59) return null;

      const parsed = new Date(year, month - 1, day, hours, minutes, 0);
      if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day ||
        parsed.getHours() !== hours ||
        parsed.getMinutes() !== minutes
      ) {
        return null;
      }

      return this.parseDate(`${dateValue.slice(0, 4)}-${dateValue.slice(4, 6)}-${dateValue.slice(6, 8)}`, `${timeValue.slice(0, 2)}:${timeValue.slice(2, 4)}`);
    }

    pad(value) {
      return String(value).padStart(2, '0');
    }

    tick() {
      if (this.sharedTarget) {
        const distance = this.sharedTarget.getTime() - Date.now();
        if (distance <= 0) {
          this.handleSharedExpiry();
          return;
        }
        this.renderTimer(distance);
        return;
      }

      const now = Date.now();
      let soonest = null;

      this.countdowns.forEach((item) => {
        const distance = item.target.getTime() - now;

        if (distance <= 0) {
          if (!item.done) {
            item.done = true;
            if (item.offer.dataset.hideExpired === 'true') {
              item.offer.classList.add('is-hidden');
              this.handleExpiredVisibility();
            }
          }
          return;
        }

        if (soonest === null || distance < soonest) soonest = distance;
      });

      /* The row always shows the soonest-ending active countdown; when that
         one expires the next deadline takes over, and the row disappears
         once no countdown is left. */
      if (soonest === null) {
        this.timerRow.hidden = true;
        this.stopClock();
        return;
      }

      this.renderTimer(soonest);
    }

    renderTimer(distance) {
      if (!this.timerEls || !this.timerRow) return;

      const days = Math.floor(distance / 86400000);
      const hours = Math.floor((distance % 86400000) / 3600000);
      const minutes = Math.floor((distance % 3600000) / 60000);
      const seconds = Math.floor((distance % 60000) / 1000);

      this.timerEls.days.textContent = this.pad(days);
      this.timerEls.hours.textContent = this.pad(hours);
      this.timerEls.mins.textContent = this.pad(minutes);
      this.timerEls.secs.textContent = this.pad(seconds);

      const showDays = days > 0;
      this.timerEls.daysUnit.hidden = !showDays;
      if (this.timerEls.daysSep) this.timerEls.daysSep.hidden = !showDays;

      this.timerRow.classList.toggle('is-urgent', distance < 86400000);
      this.timerRow.hidden = false;
    }

    handleSharedExpiry() {
      this.countdownComplete = true;
      this.stopClock();
      this.offers.forEach((offer) => offer.classList.add('is-hidden'));
      this.handleExpiredVisibility();
      if (!this.timerRow) return;
      this.timerRow.hidden = true;
      this.timerRow.classList.remove('is-urgent');
    }

    stopClock() {
      if (this.clock) {
        clearInterval(this.clock);
        this.clock = null;
      }
    }

    handleExpiredVisibility() {
      const visible = this.visibleOffers();
      if (visible.length === 0) {
        /* The element is the whole content column — only the offers list may go */
        if (this.offersList) this.offersList.style.display = 'none';
        this.stopRotation();
        return;
      }
      if (this.dataset.display === 'rotate') {
        this.currentRotation = this.currentRotation % visible.length;
        this.updateRotation();
      }
    }

    visibleOffers() {
      return this.offers.filter((offer) => !offer.classList.contains('is-hidden'));
    }

    updateRotation() {
      const visible = this.visibleOffers();
      visible.forEach((offer, i) => {
        offer.classList.toggle('is-current', i === this.currentRotation % visible.length);
      });
    }

    startRotation() {
      if (this.dataset.display !== 'rotate' || this.rotationTimer || REDUCED_MOTION.matches) return;
      if (this.visibleOffers().length <= 1) return;
      this.rotationTimer = setInterval(() => {
        const visible = this.visibleOffers();
        if (visible.length <= 1) return;
        this.currentRotation = (this.currentRotation + 1) % visible.length;
        this.updateRotation();
      }, this.rotateDelay);
    }

    stopRotation() {
      if (this.rotationTimer) {
        clearInterval(this.rotationTimer);
        this.rotationTimer = null;
      }
    }
  }

  if (!customElements.get('hero-split-slider')) {
    customElements.define('hero-split-slider', HeroSplitSlider);
  }
  if (!customElements.get('offer-bar')) {
    customElements.define('offer-bar', OfferBar);
  }
})();
